import React, { useState } from "react";
import {
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Typography,
  TablePagination,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  TableContainer,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";

import VisibilityIcon from "@mui/icons-material/Visibility";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import CollectionActions from "../common/components/CollectionActions";
import TableShimmer from "../common/components/TableShimmer";
import CollectionFab from "../settings/CollectionFab";
import ErrorDialog from "../common/components/ErrorDialog";

import { suppliersActions } from "../store";
import { getWithExpiry } from "../common/util/localstorage";
import { useEffectAsync } from "../reactHelper";

const SuppliersPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const _suppliers = useSelector((state) => state.suppliers.items);

  const [loading, setLoading] = useState(false);

  // ✅ SMART FILTER STATE (single input)
  const [filter, setFilter] = useState("");

  const [filterPays, setFilterPays] = useState("all");
  const [filterActivite, setFilterActivite] = useState("all");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedActivities, setSelectedActivities] = useState([]);

  const [opening, setOpening] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // FETCH
  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_URL_NAME}/suppliers`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${getWithExpiry("TOKEN")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        dispatch(suppliersActions.setItems(data));
      } else if (response.status === 401) {
        setErrorMsg("UNAUTHORIZED");
        setOpening(true);
        navigate("/login");
      } else {
        const text = await response.text();
        setErrorMsg(text);
        setOpening(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // UNIQUE VALUES
  const allPays = [
    ...new Set(_suppliers?.map((s) => s.pays).filter(Boolean)),
  ];

  const allActivites = [
    ...new Set(_suppliers?.flatMap((s) => s.activites || [])),
  ];

  // ✅ SMART FILTER LOGIC
  const filteredSuppliers = _suppliers?.filter((s) => {
    const k = filter.toLowerCase();

    const matchSearch =
      !k ||
      s.nom?.toLowerCase().includes(k) ||
      s.phone?.toLowerCase().includes(k) ||
      s.email?.toLowerCase().includes(k) ||
      s.pays?.toLowerCase().includes(k) ||
      s.address?.toLowerCase().includes(k) ||
      s.activites?.some((a) => a.toLowerCase().includes(k));

    const matchPays =
      filterPays === "all" || s.pays === filterPays;

    const matchActivite =
      filterActivite === "all" ||
      s.activites?.includes(filterActivite);

    return matchSearch && matchPays && matchActivite;
  });

  const paginated = filteredSuppliers?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleOpenPopup = (activites) => {
    setSelectedActivities(activites || []);
    setPopupOpen(true);
  };

  return (
    <>
      {/* TITLE */}
      <Typography
        variant="h3"
        align="center"
        sx={{
          mb: 3,
          fontWeight: 800,
          background: "linear-gradient(90deg, #002355, #0081f9)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        Fournisseurs
      </Typography>

      {/* FILTER BAR */}
      <Box
        display="flex"
        gap={2}
        mb={2}
        flexWrap="wrap"
        alignItems="center"
      >
        {/* 🔎 SMART SEARCH (TYPE ANYTHING) */}
        <TextField
          label="Recherche (nom, email, activité...)"
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 300 }}
        />

        {/* 📍 PAYS */}
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Pays</InputLabel>
          <Select
            value={filterPays}
            label="Pays"
            onChange={(e) => {
              setFilterPays(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">Tous</MenuItem>
            {allPays.map((p, i) => (
              <MenuItem key={i} value={p}>
                {p}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* ⚙️ ACTIVITE */}
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel>Activité</InputLabel>
          <Select
            value={filterActivite}
            label="Activité"
            onChange={(e) => {
              setFilterActivite(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">Toutes</MenuItem>
            {allActivites.map((a, i) => (
              <MenuItem key={i} value={a}>
                {a}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* TABLE */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#002355" }}>
              <TableCell sx={{ color: "#fff" }}>#</TableCell>
              <TableCell sx={{ color: "#fff" }}>NOM</TableCell>
              <TableCell sx={{ color: "#fff" }}>PHONE</TableCell>
              <TableCell sx={{ color: "#fff" }}>EMAIL</TableCell>
              <TableCell sx={{ color: "#fff" }}>PAYS</TableCell>
                            <TableCell sx={{ color: "#fff" }}>WEBSITE</TableCell>

              <TableCell sx={{ color: "#fff" }}>ACTIVITÉS</TableCell>
              <TableCell sx={{ color: "#fff" }}>ACTIONS</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!loading && paginated?.length > 0 ? (
              paginated.map((s, idx) => (
                <TableRow key={s._id}>
                  <TableCell>
                    {page * rowsPerPage + idx + 1}
                  </TableCell>

                  <TableCell>{s.nom}</TableCell>
                  <TableCell>{s.phone}</TableCell>
                  <TableCell>{s.email}</TableCell>
                  <TableCell>{s.pays || "—"}</TableCell>
                                    <TableCell>{s.website || "—"}</TableCell>


                  <TableCell>
                    <Chip
                      label={s.activites?.length || 0}
                      color="primary"
                      size="small"
                      sx={{ mr: 1 }}
                    />

                    {s.activites?.length > 0 && (
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleOpenPopup(s.activites)
                        }
                      >
                        <VisibilityIcon />
                      </IconButton>
                    )}
                  </TableCell>

                  <TableCell>
                    <CollectionActions
                      itemId={s._id}
                      editPath="/settings/supplier"
                      endpoint="suppliers"
                      action={suppliersActions}
                      removable
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableShimmer columns={7} />
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* PAGINATION */}
      <TablePagination
        component="div"
        count={filteredSuppliers?.length || 0}
        page={page}
        onPageChange={(e, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />

      {/* FAB */}
      <CollectionFab editPath="/settings/supplier" />

      {/* ERROR */}
      <ErrorDialog
        open={opening}
        errorMsg={errorMsg}
        onResult={() => setOpening(false)}
      />

      {/* POPUP ACTIVITIES */}
      <Dialog
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
      >
        <DialogTitle>Activités</DialogTitle>
        <DialogContent>
          {selectedActivities.length ? (
            <List>
              {selectedActivities.map((a, i) => (
                <ListItem key={i}>
                  <ListItemText primary={a} />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography>Aucune activité</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SuppliersPage;